import { z } from "zod";

export enum LocationType {
  APARTMENT = "Apartment",
  HOUSE = "House",
  OFFICE = "Office",
  OTHER = "Other",
}

export const LocationTypeLabels: Record<LocationType, string> = {
  [LocationType.APARTMENT]: "Mieszkanie",
  [LocationType.HOUSE]: "Dom",
  [LocationType.OFFICE]: "Biuro",
  [LocationType.OTHER]: "Inne",
};

const GeolocationSchema = z.object({
  latitude: z.string(),
  longitude: z.string(),
});
export type Geolocation = z.infer<typeof GeolocationSchema>;

const GeocodingConfidenceSchema = z.string().nullish();

const AddressBaseSchema = z.object({
  street_name: z.string(),
  building_number: z.string().nullish(),
  apartment_number: z.string().nullish(),
  district: z.string().nullish(),
  province: z.string().nullish(),
  postal_code: z.string().nullish(),
  city: z.string().nullish(),
  geolocation: GeolocationSchema.nullish(),
  use_geolocation_for_navigation: z.boolean().nullish(),
  geocoding_confidence: GeocodingConfidenceSchema,
  geocoding_confidence_city_level: GeocodingConfidenceSchema,
  geocoding_confidence_street_level: GeocodingConfidenceSchema,
  geocoding_confidence_building_level: GeocodingConfidenceSchema,
  geocoding_match_type: z.string().nullish(),
  geocoding_result_type: z.string().nullish(),
  location_type: z.nativeEnum(LocationType),
});

export const AddressResponseSchema = AddressBaseSchema.extend({
  id: z.number(),
});
export type AddressResponse = z.infer<typeof AddressResponseSchema>;

export const AddressAdminResponseSchema = AddressResponseSchema.extend({
  driver_note: z.string(),
});

export const AddressGeolocationUpdatePayloadSchema = z.object({
  latitude: z.string(),
  longitude: z.string(),
});
export type AddressGeolocationUpdatePayload = z.infer<
  typeof AddressGeolocationUpdatePayloadSchema
>;

export const AddressSearchResultSchema = z.object({
  formatted: z.string(),
  latitude: z.string(),
  longitude: z.string(),
  result_type: z.string().nullable(),
  street_name: z.string().nullish(),
  building_number: z.string().nullish(),
  postal_code: z.string().nullish(),
  city: z.string().nullish(),
  district: z.string().nullish(),
});
export type AddressSearchResult = z.infer<typeof AddressSearchResultSchema>;

export const AddressDeliveryFormSchema = AddressBaseSchema.extend({
  street_name: z.string(),
  postal_code: z.string().regex(/^\d{2}-\d{3}$/, {
    message: "validation.order.postal_code_format",
  }),
  city: z.string(),
  location_type: z.nativeEnum(LocationType, {
    errorMap: () => ({ message: "validation.order.location_type" }),
  }),
});

export const AddressDeliveryAdminFormSchema = AddressBaseSchema.extend({
  street_name: z.string(),
  driver_note: z.string().max(2000).optional(),
  postal_code: z
    .union([
      z.literal(""),
      z.string().regex(/^\d{2}-\d{3}$/, {
        message: "validation.order.postal_code_format",
      }),
    ])
    .nullish(),
  city: z.string().nullish(),
  district: z.string().nullish(),
  location_type: z.nativeEnum(LocationType, {
    errorMap: () => ({ message: "validation.order.location_type" }),
  }),
}).refine(
  (address) =>
    Boolean(address.postal_code?.trim()) ||
    Boolean(address.city?.trim()) ||
    Boolean(address.district?.trim()),
  {
    message: "validation.order.address_area_required",
    path: ["district"],
  },
);

export const DriverEndingAddressFormSchema = AddressBaseSchema.extend({
  street_name: z.string().nullish(),
  postal_code: z.string().nullish(),
  city: z.string().nullish(),
  location_type: z.nativeEnum(LocationType).nullish(),
});
